import { LoadingButton } from "@mui/lab";
import { Grid, MenuItem, Select, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";
import { CountryCode } from "libphonenumber-js";
import { FC, ReactNode, useEffect, useState } from "react";
import { Employer, EmployerCompanySize } from "../../models";
import { useConfig } from "../../utils/useConfig";
import { useDynamicEmployerValidationSchema } from "../../utils/useDynamicValidationSchema";
import { AddressFinderForm } from "../AddressFinderForm/AddressFinderForm";

export type EmployerFormValuesType = {
  contactFirstName: string;
  contactLastName: string;
  name: string;
  companySize: string;
  billingContact: {
    email: string;
  };
  contact: {
    phone: string;
    email: string;
    address: {
      address1: string;
      address2: string;
      city: string;
      stateOrProvince: string;
      postalCode: string;
      country: CountryCode;
    };
  };
};

export const EmployerForm: FC<{
  isCreateEmployer: boolean;
  isLoading: boolean;
  employer?: Employer;
  children: ReactNode | ReactNode[];
  onSubmit: (formValues: EmployerFormValuesType) => void;
}> = ({ isCreateEmployer, isLoading, employer, onSubmit, children }) => {
  const { config } = useConfig();

  const [country, setCountry] = useState<any>(
    employer?.contact.address.country ?? "NZ"
  );
  const companySizeOptions = [
    { label: "1-350 Employees", value: EmployerCompanySize.SMALL },
    { label: "351-1000 Employees", value: EmployerCompanySize.MEDIUM },
    { label: "1000+ Employees", value: EmployerCompanySize.LARGE }
  ];

  const validationSchema = useDynamicEmployerValidationSchema(country);

  const { resetForm, ...formik } = useFormik({
    initialValues: {
      contactFirstName: employer?.contactName?.split(" ")[0] ?? "",
      contactLastName: employer?.contactName?.split(" ")[1] ?? "",
      name: employer?.name ?? "",
      companySize: employer?.companySize ?? "",
      contact: {
        phone: employer?.contact.phone ?? "",
        email: employer?.contact.email ?? "",
        address: {
          address1: employer?.contact.address.address1 ?? "",
          address2: employer?.contact.address.address2 ?? "",
          city: employer?.contact.address.city ?? "",
          stateOrProvince: employer?.contact.address.stateOrProvince ?? "",
          postalCode: employer?.contact.address.postalCode ?? "",
          country
        }
      },
      billingContact: {
        email: employer?.billingContact?.email ?? ""
      }
    },
    validationSchema,
    onSubmit: async (values) => {
      onSubmit(values);
    }
  });

  useEffect(() => {
    setCountry(formik.values.contact.address.country);
  }, [formik.values.contact.address.country]);

  useEffect(() => {
    if (country === "NZ") {
      formik.setFieldValue("contact.address.stateOrProvince", undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              value={formik.values.name}
              onChange={formik.handleChange}
              label="Company Name"
              name="name"
              required
              fullWidth
              size="small"
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={
                formik.touched.name &&
                formik.errors.name?.replace("name", "Company Name")
              }
            />
          </Grid>
          <Grid item xs={6}>
            <Select
              displayEmpty
              value={formik.values.companySize}
              onChange={formik.handleChange}
              name="companySize"
              size="small"
              required
              fullWidth
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <Typography color="grey">Company Size*</Typography>;
                }

                return companySizeOptions.find((cso) => cso.value === selected)
                  ?.label;
              }}
              error={
                formik.touched.companySize && Boolean(formik.errors.companySize)
              }
            >
              {companySizeOptions.map((cso) => {
                return <MenuItem value={cso.value}>{cso.label}</MenuItem>;
              })}
            </Select>
          </Grid>
        </Grid>

        <Grid container spacing={2} mt={3} mb={3}>
          <Grid item xs={6}>
            <TextField
              value={formik.values.contactFirstName}
              onChange={formik.handleChange}
              label="Contact First Name"
              name="contactFirstName"
              required
              fullWidth
              size="small"
              error={
                formik.touched.contactFirstName &&
                Boolean(formik.errors.contactFirstName)
              }
              helperText={
                formik.touched.contactFirstName &&
                formik.errors.contactFirstName?.replace(
                  "contactFirstName",
                  "Contact First Name"
                )
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              value={formik.values.contactLastName}
              onChange={formik.handleChange}
              label="Contact Last Name"
              name="contactLastName"
              required
              fullWidth
              size="small"
              error={
                formik.touched.contactLastName &&
                Boolean(formik.errors.contactLastName)
              }
              helperText={
                formik.touched.contactLastName &&
                formik.errors.contactLastName?.replace(
                  "contactLastName",
                  "Contact Last Name"
                )
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              value={formik.values.contact.email}
              onChange={formik.handleChange}
              label="Contact Email"
              name="contact.email"
              required
              fullWidth
              size="small"
              error={
                formik.touched.contact?.email &&
                Boolean(formik.errors.contact?.email)
              }
              helperText={
                formik.touched.contact?.email &&
                formik.errors.contact?.email?.replace(
                  "contact.email",
                  "Contact Email"
                )
              }
            />
          </Grid>
          {!isCreateEmployer && (
            <Grid item xs={6}>
              <TextField
                value={formik.values.billingContact.email}
                onChange={formik.handleChange}
                label="Billing Email"
                name="billingContact.email"
                required={!isCreateEmployer}
                fullWidth
                size="small"
                error={
                  formik.touched?.billingContact?.email &&
                  Boolean(formik.errors?.billingContact?.email)
                }
                helperText={
                  formik.touched?.billingContact?.email &&
                  formik.errors.billingContact?.email?.replace(
                    "billingContact.email",
                    "Billing Email"
                  )
                }
              />
            </Grid>
          )}
          <Grid item xs={6}>
            <TextField
              value={formik.values.contact.phone}
              onChange={formik.handleChange}
              label="Contact Phone"
              name="contact.phone"
              required
              fullWidth
              size="small"
              type="tel"
              error={
                formik.touched.contact?.phone &&
                Boolean(formik.errors.contact?.phone)
              }
              helperText={
                formik.touched.contact?.phone &&
                formik.errors.contact?.phone?.replace(
                  "contact.phone",
                  "Contact Phone"
                )
              }
            />
          </Grid>
          {config?.ADDRESS_FINDER_KEY && ( //TODO: When building out new self service flow, add selection for country as address finder doesn't support both NZ/AU, only one or the other
            <AddressFinderForm
              formik={{ resetForm, ...formik }}
              promptEmployerAddress={false}
              required={true}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          {children}
        </Grid>

        <LoadingButton
          sx={{
            marginLeft: "auto"
          }}
          type="submit"
          variant="contained"
          color="primary"
          loading={isLoading}
        >
          {isCreateEmployer ? "Submit" : "Update"}
        </LoadingButton>
      </Grid>
    </form>
  );
};
